import React, { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

export type FilterPeriod = "all" | "day" | "week";

interface DashboardFilterContextType {
  activeFilter: FilterPeriod | undefined;
  selectedMonth: number | undefined;
  setActiveFilter: (filter: FilterPeriod) => void;
  setSelectedMonth: (month: number) => void;
}

const DashboardFilterContext = createContext<
  DashboardFilterContextType | undefined
>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useDashboardFilter = () => {
  const context = useContext(DashboardFilterContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardFilter must be used within a DashboardFilterProvider"
    );
  }
  return context;
};

interface DashboardFilterProviderProps {
  children: ReactNode;
}

export const DashboardFilterProvider: React.FC<
  DashboardFilterProviderProps
> = ({ children }) => {
  const [activeFilter, setActiveFilter] = useState<FilterPeriod | undefined>(
    "all"
  );
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(
    undefined
  );

  const handleFilterChange = (filter: FilterPeriod) => {
    setActiveFilter(filter);
    setSelectedMonth(undefined);
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    setActiveFilter(undefined);
  };

  return (
    <DashboardFilterContext.Provider
      value={{
        activeFilter,
        selectedMonth,
        setActiveFilter: handleFilterChange,
        setSelectedMonth: handleMonthChange,
      }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
};
